// ReasonField — preset-dropdown + free-text input combo for the
// reconcile merge/split dialogs (and any future dialog that takes the
// mandatory `reason` audit string).
//
// Why both: every reconciliation write writes one reconcile_audit_log
// row with a mandatory reason. In practice the same dozen phrases recur
// ("Duplicate row from extractor", "Stacked plan — 2-storey duplex",
// etc.). A plain text input forces the operator to retype them; a plain
// dropdown can't capture the long tail. The combo lets a preset
// pre-fill the input — the operator can then edit if they need to add
// a unit-specific note ("…this one is unit #03-05"). The value sent
// over the wire is always the input text, so the audit log stays
// human-readable regardless of how it was entered.

import { useState, type CSSProperties } from "react";
import { inputBase } from "@/components/ui-prims";

// PresetGroup is the dropdown shape passed by callers. Kept as a plain
// array (not a keyed enum) so each dialog can curate its own list
// without coupling to a global "all reasons everywhere" registry.
export type ReasonPreset = string;

interface Props {
  value: string;
  onChange: (next: string) => void;
  presets: ReasonPreset[];
  // placeholder shows when the operator picks "Other…" and the input
  // is empty — same string the old free-text-only input used.
  placeholder?: string;
  disabled?: boolean;
}

// Special dropdown values: "" = the disabled placeholder option,
// OTHER = "free-text mode" (preset selection cleared, input retained).
const OTHER = "__other__";

export function ReasonField({ value, onChange, presets, placeholder, disabled }: Props) {
  // Track the dropdown selection separately from the input value so
  // that switching to "Other…" doesn't blow away what the operator
  // typed before. Initial selection seeded from the inbound value so
  // a re-mount on dialog reopen lands consistent.
  const [picked, setPicked] = useState<string>(() => {
    if (presets.includes(value)) return value;
    if (value !== "") return OTHER; // arbitrary inbound text → free-text mode
    return "";
  });

  function handlePick(next: string) {
    setPicked(next);
    if (next === OTHER) {
      // Switching to Other leaves the current value in place — the
      // operator may want to start from the previously-picked preset
      // and edit it. The input becomes visible below.
      return;
    }
    if (next === "") {
      return;
    }
    // Preset chosen — set the value directly. Input stays hidden, so
    // the only way back to editing is to pick "Other…" again.
    onChange(next);
  }

  function handleType(next: string) {
    onChange(next);
    // In Other mode, the operator may type the exact preset text. Snap
    // the dropdown to that preset so the input hides and the label
    // matches reality.
    if (presets.includes(next) && picked !== next) {
      setPicked(next);
    }
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
      <select
        value={picked}
        onChange={(e) => handlePick(e.target.value)}
        disabled={disabled}
        style={selectStyle}
      >
        <option value="" disabled>
          Pick a common reason…
        </option>
        {presets.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
        <option value={OTHER}>Other (type below)…</option>
      </select>
      {picked === OTHER && (
        <input
          style={inputBase}
          value={value}
          onChange={(e) => handleType(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus
        />
      )}
    </div>
  );
}

const selectStyle: CSSProperties = {
  ...inputBase,
  // The native <select> arrow looks fine; just match the input's
  // typography so the two controls read as one field.
  appearance: "auto",
};
